import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import {
  getRequest,
  putRequest,
  putWithFormData,
} from "../../helpers/api.helper";
import { totalPropertyValueOptions } from "../../utils/constants";

const Step6Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // Add profile check
  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/common/city");
        // @ts-ignore
        setCity(response);
      } catch (error) {
        console.error(`Error fetching data:`, error);
      }
    };
    fetchData();
  }, []);

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [city, setCity] = useState([]);

  // Initialize with profile data
  const [selectedOptions, setSelectedOptions] = useState<any>({
    House_Type: profile?.houseType
      ? {
          value: profile.houseType,
          label: getHouseTypeLabel(profile.houseType),
        }
      : null,
    No_Of_Houses: profile?.numberOfHouses
      ? { value: profile.numberOfHouses, label: profile.numberOfHouses }
      : null,
    Own_House_sq_Yard: profile?.houseSqFeet
      ? { value: profile.houseSqFeet, label: `${profile.houseSqFeet} Yards` }
      : null,
    Own_House_Total_Value: profile?.houseValue
      ? {
          value: profile.houseValue,
          label: `₹${parseInt(profile.houseValue).toLocaleString("en-IN")}`,
        }
      : null,
    House_Location: profile?.houseLocation || [],
    Commercial_Shops: profile?.commercialshops
      ? { value: profile.commercialshops, label: profile.commercialshops }
      : null,
    Shops_Sq_Yards: profile?.shopssqyards
      ? { value: profile.shopssqyards, label: `${profile.shopssqyards} Yards` }
      : null,
    Monthly_Rent: profile?.monthlyRent
      ? {
          value: profile.monthlyRent,
          label: `₹${parseInt(profile.monthlyRent).toLocaleString("en-IN")}`,
        }
      : null,
    Open_Plots: profile?.openPlots
      ? { value: profile.openPlots, label: profile.openPlots.toString() }
      : null,
    Open_Plots_Sq_Yards: profile?.openPlotsSqFeet
      ? {
          value: profile.openPlotsSqFeet,
          label: `${profile.openPlotsSqFeet} Yards`,
        }
      : null,
    Open_Plots_total_value: profile?.openPlotsValue
      ? {
          value: profile.openPlotsValue,
          label: `₹${parseInt(profile.openPlotsValue).toLocaleString("en-IN")}`,
        }
      : null,
    Plot_Location: profile?.openPlotsLocation || [],
  });

  // Helper function to get house type label
  function getHouseTypeLabel(value: any) {
    const labels: any = {
      groundFloor: "Ground Floor",
      "g+1": "G+1",
      "g+2": "G+2",
      "g+3": "G+3",
      "g+4": "G+4",
      "g+5": "G+5",
      "g+6": "G+6",
      "g+7": "G+7",
      "g+8": "G+8",
      "g+9": "G+9",
      "g+10": "G+10",
    };
    return labels[value] || value;
  }

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    House_Type: {
      type: "single",
      options: [
        { value: "groundFloor", label: "Ground Floor" },
        { value: "g+1", label: "G+1" },
        { value: "g+2", label: "G+2" },
        { value: "g+3", label: "G+3" },
        { value: "g+4", label: "G+4" },
        { value: "g+5", label: "G+5" },
        { value: "g+6", label: "G+6" },
        { value: "g+7", label: "G+7" },
        { value: "g+8", label: "G+8" },
        { value: "g+9", label: "G+9" },
        { value: "g+10", label: "G+10" },
      ],
    },
    No_Of_Houses: {
      type: "single",
      options: Array.from({ length: 10 }, (_, i) => ({
        value: (i + 1).toString(),
        label: (i + 1).toString(),
      })),
    },
    Own_House_sq_Yard: {
      type: "single",
      options: Array.from(
        { length: 1000 - 70 + 1 },
        (_, index) => 70 + index
      ).map((sqYards) => ({
        value: sqYards,
        label: `${sqYards} Yards`,
      })),
    },
    Own_House_Total_Value: {
      type: "single",
      options: totalPropertyValueOptions,
    },

    House_Location: {
      type: "multi",
      options: city.map((c: any) => ({
        value: c.city,
        label: c.city,
      })),
    },
    Commercial_Shops: {
      type: "single",
      options: Array.from({ length: 10 }, (_, i) => ({
        value: (i + 1).toString(),
        label: (i + 1).toString(),
      })),
    },
    Shops_Sq_Yards: {
      type: "single",
      options: Array.from(
        { length: 1000 - 70 + 1 },
        (_, index) => 70 + index
      ).map((sqYards) => ({
        value: sqYards,
        label: `${sqYards} Yards`,
      })),
    },
    Monthly_Rent: {
      type: "single",
      options: Array.from(
        { length: 200 },
        (_, index) => 50000 + index * 50000
      ).map((value) => ({
        value: value,
        label: `₹${value.toLocaleString("en-IN")}`,
      })),
    },
    Open_Plots: {
      type: "single",
      options: Array.from({ length: 20 }, (_, i) => i + 1).map((value) => ({
        value: value,
        label: value.toString(),
      })),
    },
    Open_Plots_Sq_Yards: {
      type: "single",
      options: Array.from(
        { length: 1000 - 70 + 1 },
        (_, index) => 70 + index
      ).map((sqYards) => ({
        value: sqYards,
        label: `${sqYards} Yards`,
      })),
    },

    Open_Plots_total_value: {
      type: "single",
      options: totalPropertyValueOptions,
    },
    Plot_Location: {
      type: "multi",
      options: city.map((c: any) => ({
        value: c.city,
        label: c.city,
      })),
    },
  };

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!modalData && searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: any) => {
    setModalData({
      key,
      type: dropdownOptions[key].type,
      options: dropdownOptions[key].options,
    });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleMultiSelectToggle = (value: any) => {
    const currentValues = selectedOptions[modalData.key] || [];
    const updatedValues = currentValues.includes(value)
      ? currentValues.filter((v: any) => v !== value)
      : [...currentValues, value];

    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: updatedValues,
    }));
  };

  const handleSingleSelect = (option: any) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: option,
    }));
    closeModal();
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formDataToSend = {
      houseType: selectedOptions.House_Type?.value || "",
      houseSqFeet: selectedOptions.Own_House_sq_Yard?.value || "",
      houseValue: selectedOptions.Own_House_Total_Value?.value || "",
      monthlyRent: selectedOptions.Monthly_Rent?.value || "",
      openPlots: selectedOptions.Open_Plots?.value || "",
      openPlotsSqFeet: selectedOptions.Open_Plots_Sq_Yards?.value || "",
      openPlotsValue: selectedOptions.Open_Plots_total_value?.value || "",
      numberOfHouses: selectedOptions.No_Of_Houses?.value || "",
      commercialshops: selectedOptions.Commercial_Shops?.value || "",
      shopssqyards: selectedOptions.Shops_Sq_Yards?.value || "",
      houseLocation: selectedOptions.House_Location || [],
      openPlotsLocation: selectedOptions.Plot_Location || [],
      step1: 1,
      step2: 1,
      step3: 1,
      step4: 1,
      step5: 1,
      step6: 1,
    };
    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );
      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  /** ---------- Helper Functions ---------- **/
  const getDisplayValue = (key: any) => {
    const value = selectedOptions[key];
    const options = dropdownOptions[key]?.options || [];

    if (dropdownOptions[key]?.type === "multi") {
      if (!value || value.length === 0)
        return `Select ${key.replace(/_/g, " ")}`;
      return options
        .filter((opt: any) => value.includes(opt.value))
        .map((opt: any) => opt.label)
        .join(", ");
    } else {
      return value?.label || `Select ${key.replace(/_/g, " ")}`;
    }
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Search Bar */}
          <input
            ref={searchInputRef}
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any, index: any) => (
                <React.Fragment key={`${option.value}-${index}`}>
                  {modalData.type === "multi" ? (
                    // Multi-select checkbox
                    <div className="flex items-center space-x-2 py-2">
                      <input
                        type="checkbox"
                        checked={(
                          selectedOptions[modalData.key] || []
                        ).includes(option.value)}
                        onChange={() => handleMultiSelectToggle(option.value)}
                        className="form-checkbox h-4 w-4 text-red-600"
                      />
                      <label
                        className="block text-gray-700 cursor-pointer"
                        onClick={() => handleMultiSelectToggle(option.value)}
                      >
                        {option.label}
                      </label>
                    </div>
                  ) : (
                    // Single-select button
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                      onClick={() => handleSingleSelect(option)}
                    >
                      {option.label}
                    </button>
                  )}
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-indigo-100">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 6 -
            </h1>
            <h5 className="font-semibold text-white">Property Details</h5>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 mt-20 py-4 px-3">
          {/* All Fields */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2 capitalize">
                {key.replace(/_/g, " ")}
              </label>
              <div
                className="flex flex-col w-full border px-3 border-gray-300 py-3 cursor-pointer rounded-lg hover:shadow-lg transition-all"
                onClick={() => openModal(key)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-md font-medium">
                    {getDisplayValue(key)}
                  </span>
                  <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                </div>
              </div>
            </div>
          ))}

          <button
            type="submit"
            className="w-full bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            Update Property Details
          </button>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step6Edit;
